/**
 * Finnish Payment Barcode and QR Code Generation
 *
 * Generates virtual barcode string and EPC QR code data for tilisiirto payments.
 * These are client-safe functions that don't require server-side image generation.
 */

export interface BarcodeData {
  iban: string
  amount: number
  reference: string
  dueDate: Date
  recipientName: string
}

export interface QrCodeData extends BarcodeData {
  bic: string
}

/**
 * Generate virtual barcode string (54 characters)
 * Version 4 - IBAN with national reference number
 *
 * Format: 4[IBAN-numeric(16)][euros(6)][cents(2)]000[reference(20)][due-date(6)]
 */
export function generateVirtualBarcodeString(data: BarcodeData): string {
  // Extract numeric part from IBAN (remove FI prefix)
  const ibanNumeric = data.iban
    .replace(/\s/g, '')
    .substring(2)
    .padStart(16, '0')

  // Split amount into euros and cents
  const totalCents = Math.round(data.amount * 100)
  const euros = Math.floor(totalCents / 100)
    .toString()
    .padStart(6, '0')
  const cents = (totalCents % 100).toString().padStart(2, '0')

  // Format reference number (zero-pad to 20 digits)
  const reference = data.reference.padStart(20, '0')

  // Format due date as VVKKPP (year, month, day)
  const year = data.dueDate.getFullYear().toString().substring(2) // Last 2 digits
  const month = (data.dueDate.getMonth() + 1).toString().padStart(2, '0')
  const day = data.dueDate.getDate().toString().padStart(2, '0')
  const dueDateFormatted = `${year}${month}${day}`

  // Build 54-character barcode string (version 4)
  return `4${ibanNumeric}${euros}${cents}000${reference}${dueDateFormatted}`
}

/**
 * Generate EPC QR code data string for mobile payment apps
 *
 * Returns the newline-separated data string that should be encoded in a QR code.
 * Use a QR code library (e.g., qrcode.react) to render this data.
 */
export function generateEpcQrCodeData(data: QrCodeData): string {
  // Format IBAN without spaces
  const iban = data.iban.replace(/\s/g, '')

  // Format amount as EUR with 2 decimals
  const amount = `EUR${data.amount.toFixed(2)}`

  // Remove leading zeros from reference for QR code
  const reference = data.reference.replace(/^0+/, '')

  // Format due date as ISO 20022 ReqdExctnDt
  const year = data.dueDate.getFullYear()
  const month = (data.dueDate.getMonth() + 1).toString().padStart(2, '0')
  const day = data.dueDate.getDate().toString().padStart(2, '0')
  const dueDateInfo = `ReqdExctnDt/${year}-${month}-${day}`

  // Format recipient name: uppercase, dots replaced with spaces
  const recipientName = data.recipientName.toUpperCase().replace(/\./g, ' ')

  // Build EPC QR data (newline-separated)
  return [
    'BCD', // Service tag
    '002', // Version
    '1', // Character set (UTF-8)
    'SCT', // Identification
    data.bic, // BIC
    recipientName, // Beneficiary name
    iban, // IBAN
    amount, // Amount with currency
    '', // Purpose (optional)
    reference, // Structured reference (without leading zeros)
    '', // Unstructured message (optional)
    dueDateInfo, // Beneficiary to originator information
  ].join('\n')
}

/**
 * Format IBAN with spaces for display
 */
export function formatIban(iban: string): string {
  return iban
    .replace(/\s/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim()
}
